import { useEffect, useState } from "react";
import { getModels, createModel, toggleModel } from "../api";

const TIERS = ["low", "medium", "high"];

function TierBadge({ tier }) {
  const map = { low: "badge-low", medium: "badge-medium", high: "badge-high" };
  return <span className={`badge ${map[tier] || ""}`}>{tier}</span>;
}

export default function ModelRegistry() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", provider: "", tier: "medium", max_risk_level: "medium", description: "", capabilities: "", is_active: true });
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    getModels().then(setModels).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const caps = form.capabilities.split(",").map((s) => s.trim()).filter(Boolean);
      await createModel({ ...form, capabilities: caps });
      setShowForm(false);
      setForm({ name: "", provider: "", tier: "medium", max_risk_level: "medium", description: "", capabilities: "", is_active: true });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggle = async (id) => {
    await toggleModel(id);
    load();
  };

  return (
    <>
      <div className="page-header">
        <h1>Model Registry</h1>
        <p>All AI models available for assignment to approved requests.</p>
      </div>
      <div className="page-body">
        <div className="header-row">
          <div />
          <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "Cancel" : "+ Add Model"}
          </button>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">Register New Model</div>
            <div className="card-body">
              <form onSubmit={handleCreate}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Model Name *</label>
                    <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. claude-opus-5" required />
                  </div>
                  <div className="form-group">
                    <label>Provider *</label>
                    <input value={form.provider} onChange={(e) => set("provider", e.target.value)} placeholder="e.g. Anthropic" required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Tier</label>
                    <select value={form.tier} onChange={(e) => set("tier", e.target.value)}>
                      {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Max Risk Level Supported</label>
                    <select value={form.max_risk_level} onChange={(e) => set("max_risk_level", e.target.value)}>
                      {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={form.description} onChange={(e) => set("description", e.target.value)} style={{ minHeight: 70 }} />
                </div>
                <div className="form-group">
                  <label>Capabilities (comma-separated)</label>
                  <input value={form.capabilities} onChange={(e) => set("capabilities", e.target.value)} placeholder="e.g. reasoning, code, summarization" />
                </div>
                {error && <div className="error-msg">{error}</div>}
                <button className="btn btn-primary" type="submit">Register Model</button>
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="table-wrap">
            {loading ? (
              <div className="empty">Loading…</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Provider</th>
                    <th>Tier</th>
                    <th>Max Risk</th>
                    <th>Capabilities</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <strong>{m.name}</strong>
                        {m.description && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{m.description.slice(0, 80)}{m.description.length > 80 ? "…" : ""}</div>}
                      </td>
                      <td className="text-muted">{m.provider}</td>
                      <td><TierBadge tier={m.tier} /></td>
                      <td><TierBadge tier={m.max_risk_level} /></td>
                      <td style={{ fontSize: 12, color: "var(--muted)" }}>
                        {(m.capabilities || []).slice(0, 3).join(", ")}{(m.capabilities || []).length > 3 ? " +more" : ""}
                      </td>
                      <td>
                        <span className={`badge ${m.is_active ? "badge-approved" : "badge-rejected"}`}>
                          {m.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-outline btn-sm" onClick={() => handleToggle(m.id)}>
                          {m.is_active ? "Disable" : "Enable"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
